import * as amplify from "../util/amplify.ts";
import * as i18n from "../i18n.ts";
import * as modal from "../util/modal.ts";
import Icons from "../svg/Icons.ts";
import { formatQuantityUnit } from "../lib/unitFormat.ts";
import { type UndoableDelete } from "../lib/SyncCache.ts";

/**
 * The item as the snackbar names it, its quantity included: "2x Milk", "300g Flour".
 */
export function itemName(text :string, quantity ?:number, unit ?:string) :string {
  const label = quantity>0?formatQuantityUnit(quantity,unit):'';
  if(!label){
    return text;
  }
  //a bare count reads as a multiplier
  return (/^[\d.,]+$/.test(label)?label+'x':label)+' '+text;
}

/**
 * Confirms an item removed from a list, with an Undo. The deletion is deferred: it reaches the
 * server once the snackbar is gone without Undo (timeout, close, click on it).
 */
export function toastItemRemoved(name :string, deletion :UndoableDelete){
  return toastRemoved({text:i18n.get('catsystem.toastitemremoved',{name}),svg:Icons.ic_done},deletion);
}

/**
 * The snackbar of a deferred deletion of items, whatever their number: Undo shows them again,
 * otherwise the deletion is sent once it is gone.
 */
export async function toastRemoved(toast :Parameters<typeof modal.toastUndo>[0], deletion :UndoableDelete){
  if(await modal.toastUndo(toast)){
    deletion.undo();
    const rendered = listsRendered();
    amplify.publish("taskNeedRefresh");
    await rendered;
    revealItems(deletion.ids);
  }else{
    await deletion.commit();
    amplify.publish("taskNeedRefresh");
  }
}

/**
 * Resolves once the lists are rendered again: to be called before publishing taskNeedRefresh.
 */
export function listsRendered() :Promise<void>{
  return new Promise(resolve=>amplify.subscribe("taskRefreshed",function rendered(){
    amplify.unsubscribe("taskRefreshed",rendered);
    //the lists render asynchronously from the event
    setTimeout(resolve,50);
  }));
}

/**
 * Scrolls a long list to items just added or restored and flashes them, so that the user sees where
 * they landed (a shopping list sorts them into their category, restored ones may come back anywhere).
 * Their rendering may still be on its way.
 */
export async function revealItems(metaIds :string[]){
  for(let tries=0;tries<20;tries++){
    const rows = metaIds
      .flatMap(metaId=>[...document.querySelectorAll<HTMLElement>('.fw-todo[data-fw-meta-id="'+metaId+'"]')])
      .filter(el=>el.offsetParent!==null);
    if(rows.length){
      //the first one in the list is enough to show where they are
      rows.sort((a,b)=>a.compareDocumentPosition(b)&Node.DOCUMENT_POSITION_FOLLOWING?-1:1);
      rows[0].scrollIntoView({block:'nearest',behavior:'smooth'});
      rows.forEach(row=>{
        row.classList.remove('fw-todo-revealed');
        //restarts the animation of an item revealed twice in a row
        void row.offsetWidth;
        row.classList.add('fw-todo-revealed');
      });
      return;
    }
    await new Promise(resolve=>setTimeout(resolve,100));
  }
}

/**
 * Confirms an item added to a list, with an Undo reverting it. The server has no undo: the caller
 * gives the call that reverts what it did (delete the created item, restore a quantity...).
 */
export async function toastItemAdded(name :string, undo :()=>Promise<unknown>|void){
  if(await modal.toastUndo({text:i18n.get('catsystem.toastitemadded',{name}),svg:Icons.ic_done})){
    await undo();
    amplify.publish("taskNeedRefresh");
  }
}
